import { useMemo, useState, useEffect } from "react";

/** Default minimum trimmed length before live search filtering applies. */
export const LIVE_SEARCH_MIN_LENGTH = 2;

export type GetEffectiveSearchQueryOptions = {
  /**
   * When `true` (default), clearing the input (`rawSearchQuery === ""`) sets the effective
   * query to `""` immediately so the full list shows without waiting for debounce.
   */
  treatEmptyAsCleared?: boolean;
  minLength?: number;
};

/**
 * Derives the query string passed to filters. Pure — no React.
 * Non-empty raw input only filters once `debouncedSearchQuery.trim().length >= minLength`.
 */
export function getEffectiveSearchQuery(
  rawSearchQuery: string,
  debouncedSearchQuery: string,
  options: GetEffectiveSearchQueryOptions = {},
): string {
  const {
    treatEmptyAsCleared = true,
    minLength = LIVE_SEARCH_MIN_LENGTH,
  } = options;

  if (rawSearchQuery === "") {
    if (treatEmptyAsCleared) {
      return "";
    }
  }

  return debouncedSearchQuery.trim().length >= minLength
    ? debouncedSearchQuery
    : "";
}

/**
 * Debounces any value over a delay window. Re-exported so list/table screens
 * (Affiliates, AffiliatePerformance, AffiliatePromoCodes, AffiliateUsers,
 * AffiliatePortalDashboard) can drop their local copies and stop violating
 * DRY (AGENTS.md §5).
 *
 * Default `delayMs` of 300ms matches the affiliate-table search debounce
 * those call sites already used; `useDebouncedSearchQuery` below still
 * passes its own (typically 250ms) value explicitly.
 */
export function useDebouncedValue<T>(value: T, delayMs = 300): T {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);

  return debouncedValue;
}

export type UseDebouncedSearchQueryOptions = {
  delayMs?: number;
  minLength?: number;
  treatEmptyAsCleared?: boolean;
};

/**
 * Debounces the search field and applies min-length / clear semantics for casino game lists.
 */
export function useDebouncedSearchQuery(
  searchQuery: string,
  options: UseDebouncedSearchQueryOptions = {},
): { effectiveSearchQuery: string } {
  const {
    delayMs = 250,
    minLength = LIVE_SEARCH_MIN_LENGTH,
    treatEmptyAsCleared = true,
  } = options;

  const debouncedSearchQuery = useDebouncedValue(searchQuery, delayMs);

  const effectiveSearchQuery = useMemo(
    () =>
      getEffectiveSearchQuery(searchQuery, debouncedSearchQuery, {
        minLength,
        treatEmptyAsCleared,
      }),
    [searchQuery, debouncedSearchQuery, minLength, treatEmptyAsCleared],
  );

  return { effectiveSearchQuery };
}
